// features/profile/ConnectionsDialog.tsx — who follows a member, and who they follow.
//
// Opened from the Following / Followers counts on a profile. Pages through
// GET /users/<handle>/connections/ (30 at a time). Each row links to that person's
// profile and carries a Follow button, so the list is a way to find people, not only a
// number explained. A private account's lists are for the owner and approved followers;
// everyone else sees a plain sentence saying so.
import { useCallback, useState } from 'react';
import { useAsyncRead } from '@/hooks/useAsyncRead';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Avatar, Button, Dialog } from '@/ui';
import { Icon } from '@/icons';
import { LoadState } from '@/ui/LoadState';
import { useFollow, followButtonLabel } from '@/hooks';
import { ApiError } from '@/core/lib/apiClient';
import { usersService, type ConnectionRow } from '@/core/services/usersService';
import type { User } from '@/core/models';

export type ConnectionKind = 'followers' | 'following';

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: min(60vh, 520px);
  overflow-y: auto;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 4px;
`;

const Person = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  color: inherit;
  text-decoration: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const Text = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-family: ${({ theme }) => theme.font.body};
`;

const Name = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Sub = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Note = styled.p`
  margin: 0;
  padding: 16px 4px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 12px;
`;

function FollowControl({ row }: { row: ConnectionRow }) {
  // The follow store is hydrated app-wide, so this stays in step with every other
  // Follow button for the same person.
  const { isFollowing, isRequested, toggleFollow } = useFollow(row.handle);
  const [busy, setBusy] = useState(false);
  if (row.you) return null;
  return (
    <Button
      size="sm"
      variant={isFollowing || isRequested ? 'secondary' : 'outline'}
      disabled={busy}
      onClick={() => {
        setBusy(true);
        void Promise.resolve(toggleFollow())
          .catch(() => {})
          .finally(() => setBusy(false));
      }}
    >
      {followButtonLabel(isFollowing, isRequested)}
    </Button>
  );
}

export function ConnectionsDialog({
  user,
  kind,
  onClose,
}: {
  user: User;
  kind: ConnectionKind;
  onClose: () => void;
}) {
  // The first page is an async read (loading / error / Retry come with it). "Private"
  // is an ANSWER, not a failure, so it is returned rather than thrown.
  const loadFirst = useCallback(
    () =>
      usersService.connections(user.handle, kind, 0).then(
        (page) => ({ page, isPrivate: false }),
        (e: unknown) => {
          if (e instanceof ApiError && e.code === 'private') {
            return { page: { results: [] as ConnectionRow[], next: null }, isPrivate: true };
          }
          throw e;
        },
      ),
    [user.handle, kind],
  );
  const first = useAsyncRead(loadFirst, `${user.handle}:${kind}`, null);

  // Later pages, added by "Show more". `moreNext` overrides the first page's `next`
  // once at least one further page has been fetched.
  const [more, setMore] = useState<ConnectionRow[]>([]);
  const [moreNext, setMoreNext] = useState<number | null | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);

  const rows = [...(first.data?.page.results ?? []), ...more];
  const next = moreNext !== undefined ? moreNext : (first.data?.page.next ?? null);

  async function showMore() {
    if (next == null || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await usersService.connections(user.handle, kind, next);
      setMore((current) => [...current, ...page.results]);
      setMoreNext(page.next);
    } catch {
      // The button stays, so the member can simply try again.
    } finally {
      setLoadingMore(false);
    }
  }

  const title = kind === 'followers' ? `Followers of ${user.name}` : `${user.name} follows`;

  return (
    <Dialog open onClose={onClose} title={title}>
      {first.loading || first.error ? (
        <LoadState
          loading={first.loading}
          error={first.error}
          retry={first.retry}
          label="this list"
          cards={3}
        />
      ) : first.data?.isPrivate ? (
        <Note>This account is private. Only approved followers can see this list.</Note>
      ) : rows.length === 0 ? (
        <Note>{kind === 'followers' ? 'No followers yet.' : 'Not following anyone yet.'}</Note>
      ) : (
        <>
          <List>
            {rows.map((row) => (
              <Row key={row.handle}>
                <Person to={`/profile/${row.handle.replace(/^@/, '')}`} onClick={onClose}>
                  <Avatar name={row.name} avatarKey={row.avatarKey} size={40} />
                  <Text>
                    <Name>
                      {row.name}
                      {row.verified && <Icon name="verify" size={14} title="Verified" />}
                    </Name>
                    <Sub>{row.bio ? `${row.handle}  ${row.bio}` : row.handle}</Sub>
                  </Text>
                </Person>
                <FollowControl row={row} />
              </Row>
            ))}
          </List>
          {next != null && (
            <Footer>
              <Button
                variant="ghost"
                disabled={loadingMore}
                onClick={() => void showMore()}
              >
                {loadingMore ? 'Loading more' : 'Show more'}
              </Button>
            </Footer>
          )}
        </>
      )}
    </Dialog>
  );
}
